/**
 * Parses markdown text into a tree of sections based on heading levels.
 * Handles ##, ###, #### headings and extracts body content underneath each.
 */

export interface MarkdownSection {
  level: number;           // 1=h1, 2=h2, 3=h3, 4=h4
  title: string;           // heading text (without ## prefix)
  body: string;            // raw markdown body under this heading (before next same/higher heading)
  children: MarkdownSection[]; // nested sub-headings
}

export interface ParsedMarkdown {
  title: string | null;     // h1 title if present
  preamble: string;         // text before any h2 section
  sections: MarkdownSection[];
}

/**
 * Split markdown into lines, detect heading levels, and build a section tree.
 */
export function parseMarkdownSections(markdown: string): ParsedMarkdown {
  if (!markdown) return { title: null, preamble: '', sections: [] };

  const lines = markdown.split('\n');
  let title: string | null = null;
  const preambleLines: string[] = [];
  const flatSections: { level: number; title: string; bodyLines: string[] }[] = [];

  let currentSection: { level: number; title: string; bodyLines: string[] } | null = null;
  let foundFirstH2 = false;

  for (const line of lines) {
    const h1Match = line.match(/^#\s+(.+)/);
    const h2Match = line.match(/^##\s+(.+)/);
    const h3Match = line.match(/^###\s+(.+)/);
    const h4Match = line.match(/^####\s+(.+)/);

    if (h1Match && !h2Match && !h3Match && !h4Match) {
      title = h1Match[1].trim();
      continue;
    }

    const headingMatch = h4Match || h3Match || h2Match;
    const headingLevel = h4Match ? 4 : h3Match ? 3 : h2Match ? 2 : 0;

    if (headingMatch && headingLevel >= 2) {
      foundFirstH2 = true;
      if (currentSection) flatSections.push(currentSection);
      currentSection = { level: headingLevel, title: headingMatch[1].trim(), bodyLines: [] };
    } else if (!foundFirstH2) {
      if (line.trim() || preambleLines.length > 0) {
        preambleLines.push(line);
      }
    } else if (currentSection) {
      currentSection.bodyLines.push(line);
    }
  }
  if (currentSection) flatSections.push(currentSection);

  const sections = buildSectionTree(flatSections);

  return {
    title,
    preamble: preambleLines.join('\n').trim(),
    sections,
  };
}

function buildSectionTree(
  flat: { level: number; title: string; bodyLines: string[] }[]
): MarkdownSection[] {
  const root: MarkdownSection[] = [];
  const stack: MarkdownSection[] = [];

  for (const item of flat) {
    const section: MarkdownSection = {
      level: item.level,
      title: item.title,
      body: item.bodyLines.join('\n').trim(),
      children: [],
    };

    while (stack.length > 0 && stack[stack.length - 1].level >= section.level) {
      stack.pop();
    }

    if (stack.length > 0) {
      stack[stack.length - 1].children.push(section);
    } else {
      root.push(section);
    }

    stack.push(section);
  }

  return root;
}

/**
 * Strip leading section number from title (e.g. "1. Architecture Overview" → "Architecture Overview")
 */
export function stripSectionNumber(title: string): string {
  return title.replace(/^\d+\.\s*/, '');
}

/**
 * Extract structured key-value items from a section body.
 * Parses lines like "- **Purpose**: description text" into [{key, value}]
 */
export function parseKeyValueList(body: string): { key: string; value: string }[] {
  const results: { key: string; value: string }[] = [];
  const lines = body.split('\n');
  let current: { key: string; value: string } | null = null;

  for (const line of lines) {
    const kvMatch = line.match(/^[-*]\s+\*\*([^*]+)\*\*\s*:\s*(.+)/);
    if (kvMatch) {
      if (current) results.push(current);
      current = { key: kvMatch[1].trim(), value: kvMatch[2].trim() };
    } else if (current && line.match(/^\s+\S/)) {
      current.value += ' ' + line.trim();
    } else {
      if (current) results.push(current);
      current = null;
    }
  }
  if (current) results.push(current);

  return results;
}

/**
 * Extract simple bullet list items from markdown body text.
 */
export function parseBulletList(body: string): string[] {
  return body
    .split('\n')
    .filter(l => l.match(/^[-*]\s+/))
    .map(l => l.replace(/^[-*]\s+/, '').trim())
    .filter(Boolean);
}

/**
 * Extract paragraphs (non-list, non-heading blocks) from markdown body text.
 */
export function parseParagraphs(body: string): string[] {
  const paragraphs: string[] = [];
  let current: string[] = [];

  for (const line of body.split('\n')) {
    if (line.match(/^[-*]\s+/) || line.match(/^#{1,4}\s+/) || line.match(/^\|/)) {
      if (current.length > 0) {
        paragraphs.push(current.join(' ').trim());
        current = [];
      }
    } else if (line.trim() === '') {
      if (current.length > 0) {
        paragraphs.push(current.join(' ').trim());
        current = [];
      }
    } else if (line.match(/^>\s*/)) {
      if (current.length > 0) {
        paragraphs.push(current.join(' ').trim());
        current = [];
      }
      paragraphs.push(line.replace(/^>\s*/, '').trim());
    } else {
      current.push(line.trim());
    }
  }
  if (current.length > 0) {
    paragraphs.push(current.join(' ').trim());
  }

  return paragraphs.filter(Boolean);
}

/**
 * Strip markdown bold/italic/code formatting for plain-text rendering.
 */
export function stripMarkdownFormatting(text: string): string {
  return text
    .replace(/\*\*([^*]+)\*\*/g, '$1')
    .replace(/\*([^*]+)\*/g, '$1')
    .replace(/__([^_]+)__/g, '$1')
    .replace(/_([^_]+)_/g, '$1')
    .replace(/`([^`]+)`/g, '$1');
}
